import React, { useMemo } from 'react';
import first from 'lodash.first';
import delve from 'dlv';
import { PromiseState } from 'react-refetch';

import KPISkeletonLoader from 'components/utility/ContentLoader/KPILoader/KPILoader';

import {
  KPIListWrapper,
  KPIList,
  Flex,
} from 'components/Overview/KPIs/shared.style';
import KPI from '../shared/KPI';

interface IPlaylistKPIsItem {
  data: {
    playlist_count: number;
    playlist_stream_count_last_week_to_date: number;
    playlist_stream_count_this_week_to_date: number;
    playlist_stream_wow_in_progress: number;
    skiprate_this_week_to_date: number;
  };
  entity: {
    id: string;
    type: string;
  };
}

interface IPlaylistKPIs {
  kpiData: PromiseState<IPlaylistKPIsItem[]>;
  isPending: boolean;
}

/**
 * This component renders KPI's for the Playlists overview block.
 *
 * @param props
 * @param {PromiseState} props.kpiData refetch PromiseState
 */
const PlaylistKPIs: React.FC<IPlaylistKPIs> = (props) => {
  const { kpiData, isPending } = props;

  const kpis = useMemo(() => {
    if (kpiData.fulfilled) {
      const data = kpiData.value;
      return first(data);
    }
  }, [kpiData]);

  const playlistCount = useMemo(() => {
    return delve(kpis || {}, ['data', 'playlist_count'], null);
  }, [kpis]);

  const streamCount = useMemo(() => {
    return delve(
      kpis || {},
      ['data', 'playlist_stream_count_this_week_to_date'],
      null
    );
  }, [kpis]);

  const streamChange = delve(
    kpis || {},
    ['data', 'playlist_stream_wow_in_progress'],
    null
  );
  const skipRate = delve(
    kpis || {},
    ['data', 'skiprate_this_week_to_date'],
    null
  );

  if (isPending) {
    return <KPISkeletonLoader />;
  }

  return (
    <Flex data-pw="playlists-kpi">
      <KPIListWrapper>
        <KPIList>
          <KPI value={playlistCount} title="Playlist count" />
          <KPI
            value={streamCount}
            change={streamChange}
            title="Playlist streams"
          />
          <KPI
            value={skipRate}
            title="Skiprate"
            onlyShowAbbrev={true}
            type="percentage"
          />
        </KPIList>
      </KPIListWrapper>
    </Flex>
  );
};

export default PlaylistKPIs;
